import { Button, Modal, Progress, Spin, Upload } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import elementalLoadingLogo from "../assets/image/elemental 'e'.svg";
import elementalLoadingOutline from "../assets/image/outline 'e'.svg";
import { HomeContext } from "../containers/Home";
import {
	deleteFiles,
	listFolderItems,
	uploadFiles,
} from "../services/api-server/welldocs";
import { alertMessage } from "../utils/alert";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import { getSharePointErrorMessage } from "../utils/sharepointErrors";
import { socket } from "../utils/socket";
import { capitalizeFirstLetter } from "../utils/string_convert_helper";

/**
 * A file name as SharePoint will compare it: the server replaces the
 * characters SharePoint rejects and trims, and names are case-insensitive.
 */
const uploadNameKey = (name: string) =>
	name
		.replace(/["*:<>?/\\|]/g, "_")
		.trim()
		.toLowerCase();

const DeliverablesSharepointComponent = ({
	deliverableData,
	taskData,
}: {
	deliverableData: any;
	taskData: any;
}) => {
	const context: any = useContext(HomeContext);
	const location = useLocation();
	const [fileList, setFileList] = useState<any>([]);
	const [fileListToUpload, setFileListToUpload] = useState<any>([]);
	const [currentFolderId, setCurrentFolderId] = useState<any>(null);
	const [currentContainerId, setCurrentContainerId] = useState<any>(null);
	const [uploadLength, setUploadLength] = useState<any>(0);

	const [tempRecord, setTempRecord] = useState<any>({});
	//Confirmation Modal
	const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isDeleteMultiple, setIsDeleteMultiple] = useState<boolean>(false);

	const [confirmUploadModalOpen, setConfirmUploadModalOpen] =
		useState<boolean>(false);

	//Loading Modal
	const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
	const [actionType, setActionType] = useState<string>("");
	const [loadingPercentage, setLoadingPercentage] = useState<number>(0);

	const [fetchingFiles, setFetchingFiles] = useState<boolean>(false);

	useEffect(() => {
		if (deliverableData?._id && deliverableData?.folder_id) {
			let tenantKey = context.currentTenantKey;
			let container_id = context.currentTenantData?.container_id;

			setFetchingFiles(true);
			listFolderItems(
				tenantKey,
				deliverableData?._id,
				deliverableData?.folder_id,
				container_id,
				deliverableData?.deliverable_name,
				"deliverable"
			)
				.then((response: any) => {
					setCurrentContainerId(
						context.currentTenantData?.container_id || response.containerid
					);
					setCurrentFolderId(deliverableData?.folder_id || response.folderid);
					setFileList(response.items || []);
					console.log("FILE LIST", response.items);
				})
				.catch((error: any) => {
					alertMessage(
						"error",
						"An unexpected error has occurred for documents, please contact administrator."
					);
					console.log(error);
				})
				.finally(() => {
					setFetchingFiles(false);
				});
		} else {
			setFileList([]);
			setCurrentContainerId(context.currentTenantData?.container_id || null);
			setCurrentFolderId(null);
			setFetchingFiles(false);
		}
	}, [deliverableData]);

	// Handle file uploads
	useEffect(() => {
		if (
			fileListToUpload.length > 0 &&
			uploadLength > 0 &&
			fileListToUpload.length === uploadLength
		) {
			//Check for filename duplicates
			let filenames = fileListToUpload.map((file: any) =>
				uploadNameKey(file.name)
			);
			if (filenames) {
				if (
					fileList.some((file: any) =>
						filenames.includes(uploadNameKey(file.name))
					)
				) {
					setConfirmUploadModalOpen(true);
				} else {
					handleFileUpload(fileListToUpload);
				}
			}
		}
	}, [uploadLength, fileListToUpload]);

	useEffect(() => {
		socket.on("ACTION_PROGRESS", (payload: any) => {
			setLoadingPercentage(payload);
		});

		Emitter.on("ACTION_PROGRESS", (percentage: any) => {
			setLoadingPercentage(percentage);
		});

		return () => {
			socket.removeListener("ACTION_PROGRESS");
			Emitter.remove("ACTION_PROGRESS");
		};
	}, []);

	const deleteFileModal = () => {
		let filetype = tempRecord.file ? "file" : "folder";

		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={deleteModalOpen}
				title={`Delete ${isDeleteMultiple ? "file(s)" : filetype}`}
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setDeleteModalOpen(false);
								setTempRecord({});
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							onClick={() =>
								handleFileDelete(
									[tempRecord?.id],
									isDeleteMultiple,
									tempRecord,
									filetype,
									tempRecord?.index
								)
							}
						>
							Delete
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>
						Are you sure you want to permanently delete{" "}
						{isDeleteMultiple ? "these files" : `this ${filetype}`}? Once
						deleted, it cannot be restored.
					</span>
				</div>
			</Modal>
		);
	};

	const confirmUploadModal = () => {
		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				styles={{ header: { padding: "12px 16px" } }}
				open={confirmUploadModalOpen}
				title={`Upload confirmation`}
				footer={() => (
					<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
						<Button
							onClick={() => {
								setFileListToUpload([]);
								setUploadLength(0);
								setConfirmUploadModalOpen(false);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							onClick={() => {
								setConfirmUploadModalOpen(false);
								handleFileUpload(fileListToUpload);
							}}
						>
							Proceed
						</Button>
					</div>
				)}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<span>
						A file with the same name has already been uploaded. Uploading it
						again will replace the existing file. Do you wish to proceed?
					</span>
				</div>
			</Modal>
		);
	};

	const loadingModal = () => {
		let filetype = tempRecord.file ? "file" : "folder";
		let modalTitle = "";
		if (actionType === "upload") {
			modalTitle = "File upload";
		} else if (actionType === "delete") {
			modalTitle = `Delete ${isDeleteMultiple ? "file(s)" : filetype}`;
		}

		return (
			<Modal
				closable={false}
				width={"40%"}
				centered
				title={modalTitle}
				styles={{
					header: { padding: "12px 30px" },
					body: {
						padding: "30px",
						minHeight: "350px",
						height: "350px",
						width: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					},
				}}
				open={loadingModalOpen}
				footer={null}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						top: 0,
						insetInlineStart: 0,
						height: "80%",
						width: "100%",
						margin: 0,
					}}
				>
					<img
						className="no-visual-search"
						style={{ position: "absolute", height: "40%" }}
						src={elementalLoadingLogo}
					/>
					<img
						className="spin no-visual-search"
						style={{ position: "absolute", height: "55%" }}
						src={elementalLoadingOutline}
					/>
				</div>
				<div style={{ width: "100%" }}>
					<span>
						{actionType === "upload" ? "Uploading" : "Deleting"} files...
					</span>
					<Progress percent={loadingPercentage}></Progress>
				</div>
			</Modal>
		);
	};

	const handleFileUpload = (fileList: any) => {
		console.log("CURRENT FOLDER ID", currentFolderId);
		console.log("CURRENT CONTAINER ID", currentContainerId);
		let userInfo = context.userInfo.user;
		let user = { email: userInfo.email, id: userInfo._id };
		setActionType("upload");
		setLoadingPercentage(0);
		setLoadingModalOpen(true);
		try {
			uploadFiles(
				fileList,
				context.currentTenantKey,
				"deliverable",
				deliverableData?._id,
				deliverableData?.deliverable_name,
				currentFolderId,
				currentContainerId,
				[],
				user,
				socket.id,
				deliverableData
			)
				.then((response: any) => {
					console.log("RESPONSE FROM UPLOAD FILES", response);
					setFileListToUpload([]);
					setLoadingModalOpen(false);
					setCurrentFolderId(response?.folder_id);
					// The server uploads into the deliverable's stored folder, which
					// may differ from the one this page last loaded
					if (response?.folder_id) {
						deliverableData.folder_id = response.folder_id;
					}
					const failedUploads = response?.failed_uploads || [];
					listFolderItems(
						context.currentTenantKey,
						deliverableData?._id,
						response?.folder_id || currentFolderId,
						currentContainerId || response?.container_id,
						deliverableData?.deliverable_name,
						"deliverable"
					).then((response: any) => {
						// setFileModalOpen(false);
						setFileList(response.items || []);
						if (failedUploads.length > 0) {
							const messages = failedUploads.map((failure: any) =>
								getSharePointErrorMessage(failure?.error, failure?.file_name)
							);
							alertMessage(
								"warning",
								`${failedUploads.length} file(s) could not be uploaded`,
								messages.join("\n")
							);
						} else {
							alertMessage(
								"success",
								`${fileList.length} file(s) have been successfully uploaded`
							);
						}
					});
				})
				.catch((error: any) => {
					setFileListToUpload([]);
					setLoadingModalOpen(false);
					const message = getSharePointErrorMessage(error);
					alertMessage(
						"error",
						`${fileList.length} file(s) could not be uploaded`,
						message
					);
				});
		} catch (error: any) {
			setFileListToUpload([]);
			setLoadingModalOpen(false);
			const message = getSharePointErrorMessage(error);
			alertMessage(
				"error",
				`${fileList.length} file(s) could not be uploaded`,
				message
			);
		}
	};

	const handleFileDelete = (
		idList: any,
		multiple: boolean,
		record: any = null,
		filetype: any = null,
		index: any = -1
	) => {
		console.log(idList, multiple, record, filetype, index);
		setDeleteModalOpen(false);
		setLoadingModalOpen(true);
		setActionType("delete");
		setLoadingPercentage(0);
		deleteFiles(
			context.currentTenantKey,
			idList,
			currentContainerId,
			socket.id,
			{
				type: "deliverable",
				...deliverableData,
				user: context.userInfo.user,
				filename: record.name,
			}
		)
			.then((response: any) => {
				console.log("RESPONSE FROM DELETE FILES", response);
				console.log("FILE LIST", fileList);
				console.log("ID LIST", idList);
				let foundRootIndex = fileList.findIndex(
					(record: any) => record.id === idList[0]
				);
				if (foundRootIndex != -1) {
					let _newFolderFiles = [...fileList];
					_newFolderFiles.splice(foundRootIndex, 1);
					setFileList(_newFolderFiles || []);
				}

				setLoadingModalOpen(false);
				alertMessage(
					"success",
					`${capitalizeFirstLetter(filetype)} has been successfully deleted`
				);
			})
			.catch((error: any) => {
				setDeleteModalOpen(false);
				setLoadingModalOpen(false);
				const message = getSharePointErrorMessage(error, record?.name);
				alertMessage(
					"error",
					`${capitalizeFirstLetter(filetype)} could not be deleted`,
					message
				);
			});
	};

	const handleRemove = (file: any) => {
		setFileList((prevList: any) =>
			prevList.filter((item: any) => item.uid !== file.uid)
		);
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return dayjs(dateString).format("DD MMM YYYY");
	};

	const groupFilesByDate = (files: any[]) => {
		const sortedFiles = [...files].sort(
			(a, b) =>
				new Date(b.lastModifiedDateTime).getTime() -
				new Date(a.lastModifiedDateTime).getTime()
		);

		const groupedFiles = sortedFiles.reduce(
			(acc, file) => {
				const date = formatDate(file.lastModifiedDateTime);
				if (!acc[date]) {
					acc[date] = [];
				}
				acc[date].push(file);
				return acc;
			},
			{} as Record<string, any[]>
		);

		return groupedFiles;
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
			<Upload
				multiple
				beforeUpload={() => false}
				fileList={fileListToUpload}
				onRemove={handleRemove}
				onChange={(info: any) => {
					console.log(info);
					setUploadLength(info.fileList.length);
					setFileListToUpload((prevList: any) => [...prevList, info.file]);
				}}
				itemRender={() => <></>}
			>
				<Button
					icon={GetAntIcon("upload")}
					disabled={
						(deliverableData?.status !== "New" &&
							deliverableData?.status !== "In progress") ||
						taskData?.closed == true ||
						deliverableData?.closed == true
					}
					// onClick={() => {
					// 	setFileModalOpen(true);
					// }}
				>
					Upload file
				</Button>
			</Upload>
			<div>
				{fetchingFiles ? (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Spin />
					</div>
				) : (
					Object.entries(groupFilesByDate(fileList)).map(
						([date, files]: any) => (
							<div key={date} style={{ display: "flex", marginBottom: "10px" }}>
								<div style={{ width: "100px", padding: "8px" }}>{date}</div>
								<div style={{ flex: 1 }}>
									{files.map((file: any) => (
										<div
											key={file.id}
											style={{
												display: "flex",
												alignItems: "center",
												padding: "8px",
												gap: "12px",
											}}
										>
											<div
												style={{
													flex: 1,
													border: "1px solid #6c6c6c",
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
												}}
											>
												<a
													style={{
														width: "100%",
														height: "100%",
														padding: "8px",
													}}
													onClick={() => {
														// if (
														// 	(deliverableData.status === "New" ||
														// 		deliverableData.status === "In progress") &&
														// 	deliverableData?.closed !== true
														// ) {
														// 	console.log("FILE", file);
														// 	if (
														// 		fileTools.checkSharepointEdittable(
														// 			file.file.mimeType
														// 		)
														// 	) {
														// 		collabo(
														// 			context.currentTenantKey,
														// 			file.id,
														// 			currentContainerId
														// 		).then((response: any) => {
														// 			console.log(response);
														// 			window.open(response?.link.webUrl, "__blank");
														// 		});
														// 	} else {
														// 		// preview file
														// 		const _record = encodeURIComponent(file.id);
														// 		const _container =
														// 			encodeURIComponent(currentContainerId);
														// 		const new_path = `${location.pathname}/preview?record=${_record}&container=${_container}`;
														// 		window.open(new_path, "_blank");
														// 	}
														// } else {
														// preview file
														const _record = encodeURIComponent(file.id);
														const _container =
															encodeURIComponent(currentContainerId);
														const new_path = `${location.pathname}/preview?record=${_record}&container=${_container}`;
														window.open(new_path, "_blank");
														// }
													}}
												>
													{file.name}
												</a>
												<div
													style={{
														cursor: "pointer",
														height: "100%",
														padding: "8px 11px",
														borderLeft: "1px solid #6c6c6c",
													}}
													onClick={() => {
														if (file[`@microsoft.graph.downloadUrl`]) {
															window.open(
																file[`@microsoft.graph.downloadUrl`],
																"_self"
															);
														}
													}}
												>
													{GetAntIcon("download")}
												</div>
											</div>

											{(deliverableData.status === "New" ||
												deliverableData.status === "In progress") &&
												deliverableData?.closed !== true &&
												taskData.closed !== true && (
													<div style={{ textAlign: "center" }}>
														<span
															style={{ cursor: "pointer" }}
															onClick={() => {
																setTempRecord(file);
																setIsDeleteMultiple(false);
																setDeleteModalOpen(true);
															}}
														>
															X
														</span>
													</div>
												)}
										</div>
									))}
								</div>
							</div>
						)
					)
				)}
			</div>
			{loadingModal()}
			{deleteFileModal()}
			{confirmUploadModal()}
		</div>
	);
};

export default DeliverablesSharepointComponent;
